import dynamic from 'next/dynamic'

import type {
  SectionsAppComponent,
  SectionsBrandsComponent,
  SectionsDeferralCalculatorComponent,
  SectionsEshopsComponent,
  SectionsFaqComponent,
  SectionsFileListComponent,
  SectionsFooterComponent,
  SectionsInformationBoxComponent,
  SectionsInstallmentsCalculatorComponent,
  SectionsPricingPlanComponent,
  SectionsQuotesCarouselComponent,
  SpecializedAppComponent,
  SpecializedAutoRatesPageConfigurationComponent,
  SpecializedBrandsComponent,
  SpecializedDeferralCalculatorComponent,
  SpecializedEshopsComponent,
  SpecializedFaqComponent,
  SpecializedFileListComponent,
  SpecializedFooterComponent,
  SpecializedInformationBoxComponent,
  SpecializedInstallmentsCalculatorComponent,
  SpecializedPricingPlanComponent,
  SpecializedQuotesCarouselComponent,
} from '@twisto/api-cms'

import {
  AppSection,
  Brands,
  Eshops,
  Faq,
  FileList,
  Footer,
  InformationBox,
  PricingPlan,
  QuotesCarousel,
} from '../sections'

const InstallmentsCalculator = dynamic(
  () => import('../sections/installments-calculator'),
  {
    ssr: false,
  }
)

const DeferralCalculator = dynamic(
  () => import('../sections/deferral-calculator'),
  {
    ssr: false,
  }
)

const COMPONENTS_TYPE = {
  SPECIALIZED_FAQ: 'specialized.faq',
  SPECIALIZED_APP: 'specialized.app',
  SPECIALIZED_FILE_LIST: 'specialized.file-list',
  SPECIALIZED_ESHOPS: 'specialized.eshops',
  SPECIALIZED_PRICING_PLAN: 'specialized.pricing-plan',
  SPECIALIZED_BRANDS: 'specialized.brands',
  SPECIALIZED_QUOTES_CAROUSEL: 'specialized.quotes-carousel',
  SPECIALIZED_INFORMATION_BOX: 'specialized.information-box',
  SPECIALIZED_DEFERRAL_CALCULATOR: 'specialized.deferral-calculator',
  SPECIALIZED_INSTALLMENTS_CALCULATOR: 'specialized.installments-calculator',
  SPECIALIZED_AUTO_RATES_PAGES_CONFIGURATION:
    'specialized.auto-rates-page-configuration',
  SPECIALIZED_FOOTER: 'specialized.footer',
} as const

export type SpecializedType =
  | (SpecializedFaqComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_FAQ']
    })
  | (SpecializedAppComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_APP']
    })
  | (SpecializedFileListComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_FILE_LIST']
    })
  | (SpecializedEshopsComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_ESHOPS']
    })
  | (SpecializedPricingPlanComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_PRICING_PLAN']
    })
  | (SpecializedBrandsComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_BRANDS']
    })
  | (SpecializedQuotesCarouselComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_QUOTES_CAROUSEL']
    })
  | (SpecializedInformationBoxComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_INFORMATION_BOX']
    })
  | (SpecializedDeferralCalculatorComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_DEFERRAL_CALCULATOR']
    })
  | (SpecializedInstallmentsCalculatorComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_INSTALLMENTS_CALCULATOR']
    })
  | (SpecializedFooterComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_FOOTER']
    })
  | (SpecializedAutoRatesPageConfigurationComponent & {
      __component: (typeof COMPONENTS_TYPE)['SPECIALIZED_AUTO_RATES_PAGES_CONFIGURATION']
    })

type SpecializedProps = {
  specialized?: SpecializedType[]
}

export const Specialized = ({
  specializedData,
}: {
  specializedData: SpecializedType
}) => {
  switch (specializedData.__component) {
    case 'specialized.faq':
      return <Faq {...(specializedData as unknown as SectionsFaqComponent)} />
    case 'specialized.app':
      return (
        <AppSection {...(specializedData as unknown as SectionsAppComponent)} />
      )
    case 'specialized.file-list':
      return (
        <FileList
          {...(specializedData as unknown as SectionsFileListComponent)}
        />
      )
    case 'specialized.eshops':
      return (
        <Eshops {...(specializedData as unknown as SectionsEshopsComponent)} />
      )
    case 'specialized.pricing-plan':
      return (
        <PricingPlan
          {...(specializedData as unknown as SectionsPricingPlanComponent)}
        />
      )
    case 'specialized.brands':
      return (
        <Brands {...(specializedData as unknown as SectionsBrandsComponent)} />
      )
    case 'specialized.quotes-carousel':
      return (
        <QuotesCarousel
          {...(specializedData as unknown as SectionsQuotesCarouselComponent)}
        />
      )
    case 'specialized.information-box':
      return (
        <InformationBox
          {...(specializedData as unknown as SectionsInformationBoxComponent)}
        />
      )
    case 'specialized.deferral-calculator':
      return (
        <DeferralCalculator
          {...(specializedData as unknown as SectionsDeferralCalculatorComponent)}
        />
      )
    case 'specialized.installments-calculator':
      return (
        <InstallmentsCalculator
          {...(specializedData as unknown as SectionsInstallmentsCalculatorComponent)}
        />
      )
    case 'specialized.footer':
      return (
        <Footer {...(specializedData as unknown as SectionsFooterComponent)} />
      )
    default:
      return null
  }
}

export const SpecializedComponents = ({ specialized }: SpecializedProps) => (
  <>
    {specialized?.map((item, index) => (
      <Specialized key={`${item.__component}${index}`} specializedData={item} />
    ))}
  </>
)
